/**
 * 「顧客へ発送」ダイアログ (2026-10-30〜)。設計: docs/04_design/fulfillment_inspection_mockup ④
 *
 * 検品済 (発送待ち) の発注だけ押せる。配送業者・追跡番号を入れて「発送する」と:
 *   - 入荷・検品のシリアル等を引き継いだ発送の記録が「配送中」で作られる
 *   - 顧客へ「出荷通知」メール (配送業者・追跡番号つき) が届く
 *   - 商談の段階が「発送・納品」に進む
 * (いずれも onPurchaseOrderShipped が行う)
 */
import { buildDeliveryLineItems, inspectionProgress, serialFieldLabel } from '@goenhub/shared';
import { Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { type PurchaseOrderRecord, useShipPurchaseOrder } from '@/hooks/useSalesOps';

const CARRIERS = ['ヤマト運輸', '佐川急便', '日本郵便', '西濃運輸', 'その他'] as const;
const OTHER = 'その他';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  po: PurchaseOrderRecord;
  isTimeWaver: boolean;
  contactName?: string | null;
}

export function ShipToCustomerDialog({
  open,
  onOpenChange,
  dealId,
  po,
  isTimeWaver,
  contactName,
}: Props) {
  const ship = useShipPurchaseOrder();
  const [carrier, setCarrier] = useState<string>(CARRIERS[0]);
  const [carrierOther, setCarrierOther] = useState('');
  const [tracking, setTracking] = useState('');

  useEffect(() => {
    if (open) {
      setCarrier(CARRIERS[0]);
      setCarrierOther('');
      setTracking('');
    }
  }, [open]);

  const lines = useMemo(() => buildDeliveryLineItems(po, isTimeWaver), [po, isTimeWaver]);
  const progress = inspectionProgress(po, isTimeWaver);
  const carrierName = carrier === OTHER ? carrierOther.trim() : carrier;
  const canShip = po.status === 'inspected' && progress.ready && carrierName.length > 0;
  const who = contactName ? `${contactName} 様` : '顧客';

  const handleShip = async () => {
    try {
      await ship.mutateAsync({
        dealId,
        poId: po.id,
        carrierName,
        trackingNumber: tracking.trim() || null,
      });
      toast.success(
        `顧客へ発送しました。${who}へ出荷通知を送り、商談を「発送・納品」に進めます（反映まで数秒かかります）。`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(`発送に失敗しました: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>顧客へ発送</DialogTitle>
          <DialogDescription>
            {contactName ? `${contactName} 様 · ` : ''}
            <span className="font-mono">{po.poNumber}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ship-carrier" className="text-xs">
                配送業者
              </Label>
              <select
                id="ship-carrier"
                className="w-full h-[34px] rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-2 text-[13px] text-[var(--text-primary)]"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
              >
                {CARRIERS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              {carrier === OTHER && (
                <Input
                  aria-label="配送業者の名前"
                  placeholder="配送業者の名前"
                  value={carrierOther}
                  onChange={(e) => setCarrierOther(e.target.value)}
                />
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ship-tracking" className="text-xs">
                追跡番号
              </Label>
              <Input
                id="ship-tracking"
                className="font-mono"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
              />
              {!tracking.trim() && (
                <p className="text-[11px] text-[var(--text-muted)]">
                  空のままでも発送できます（出荷通知の追跡番号は空欄になります）。
                </p>
              )}
            </div>
          </div>

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-[var(--text-primary)]">
              シリアル（検品時の入力を引き継ぎ）
            </h3>
            <ul className="rounded-md border border-[var(--border)] divide-y divide-[var(--border)] text-xs">
              {lines.map((line, li) =>
                line.deviceKind === 'none' ? (
                  <li key={li} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="text-[var(--text-secondary)]">
                      {line.name} ×{line.quantity}
                    </span>
                    <span className="text-[var(--text-muted)]">シリアルなし</span>
                  </li>
                ) : (
                  line.serialNumbers.map((s, k) => (
                    <li
                      key={`${li}-${k}`}
                      className="flex items-center justify-between gap-2 px-3 py-2"
                    >
                      <span className="text-[var(--text-secondary)]">
                        {line.name} #{k + 1}
                        {line.deviceKind === 'dell_pc' && (
                          <span className="text-[var(--text-muted)]">
                            {' '}
                            ({serialFieldLabel('dell_pc')})
                          </span>
                        )}
                      </span>
                      <span
                        className={`font-mono ${s ? 'text-[var(--text-primary)]' : 'text-[var(--accent-amber)]'}`}
                      >
                        {s || '未入力'}
                      </span>
                    </li>
                  ))
                ),
              )}
            </ul>
          </section>

          {po.status !== 'inspected' || !progress.ready ? (
            <p className="rounded-md border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] px-3 py-2 text-xs text-[var(--accent-amber)]">
              検品・シリアルの入力がそろっていないため、まだ発送できません。「入荷・検品を記録」で入力してください。
            </p>
          ) : (
            <p className="rounded-md border border-[var(--accent)] bg-[var(--accent-subtle)] px-3 py-2 text-xs leading-relaxed text-[var(--text-primary)]">
              「発送する」を押すと、{who}
              へ「出荷通知」メール（配送業者・追跡番号つき）が送られ、商談の段階が「発送・納品」に進みます。送ったメールは取り消せません。
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={ship.isPending}>
            キャンセル
          </Button>
          <Button variant="primary" onClick={handleShip} disabled={!canShip || ship.isPending}>
            <Truck className="size-4" />
            {ship.isPending ? '発送中…' : '発送する（出荷通知を送る）'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
